
import { Component, Vue } from "vue-property-decorator";
import AdminTable from "@/components/admin/AdminTable.vue";
import AdminSteamBotSelector from "@/components/admin/AdminSteamBotSelector.vue";
import AdminSkinCell from "@/components/admin/AdminSkinCell.vue";
import AdminBadge from "@/components/admin/AdminBadge.vue";
import AdminOfferStateBadge from "@/components/admin/AdminOfferStateBadge.vue";
import * as _ from "lodash";
import gql from "graphql-tag";
import copyToClipboard from 'quasar/src/utils/copy-to-clipboard.js';;
import { fetchSteamItems } from "@/helpers/admin-steam-items";
import multipleMutate from "@/mixins/multipleMutate";

// Сколько скинов оффера видно сразу; остальные — по «Ещё N»
const OFFER_ITEMS_PREVIEW = 3;

// Doppler, Gamma Doppler… — в формате «Содержит» из «Фильтра»
const DOPPLER_CONDITION = { $regex: "(?i)Doppler" };

// SteamBotOfferType на бэкенде
const OFFER_TYPES = {
  DEPOSIT: { label: "Депозит", icon: "fas fa-arrow-down", color: "positive" },
  WITHDRAW: { label: "Вывод", icon: "fas fa-arrow-up", color: "primary" }
};

interface OfferItem {
  name: string;
  price: number;
  count: number;
}

@Component({
  components: {
    AdminTable,
    AdminSkinCell,
    AdminBadge,
    AdminOfferStateBadge,
    AdminSteamBotSelector
  },
  mixins: [multipleMutate],
  apollo: {
    // Партнёры — чтобы показывать название вместо partnerUserId
    partners: {
      query: gql`
        query {
          getAllPartners {
            _id
            name
            avatar
          }
        }
      `,
      fetchPolicy: "no-cache",
      update: data => (data && data.getAllPartners) || []
    }
  }
})
export default class AdminSteamBotOffer extends Vue {
  selectedSteamBot: any = null;
  selected: any[] = [];
  partners: any[] = [];
  OFFER_ITEMS_PREVIEW = OFFER_ITEMS_PREVIEW;
  OFFER_TYPES = OFFER_TYPES;
  DOPPLER_CONDITION = DOPPLER_CONDITION;

  // Вкладки: все офферы (по умолчанию), депозиты и выводы
  tabs = [
    {
      name: "all",
      label: "Все",
      icon: "fas fa-layer-group",
      condition: {}
    },
    {
      name: "deposit",
      label: "Депозиты",
      icon: "fas fa-arrow-down",
      accent: "positive",
      condition: { type: "DEPOSIT" }
    },
    {
      name: "withdraw",
      label: "Выводы",
      icon: "fas fa-arrow-up",
      condition: { type: "WITHDRAW" }
    }
  ];
  // name → { image, rarityColor }; null — скина нет в базе
  steamItems: Record<
    string,
    { image?: string; rarityColor?: string } | null
  > = {};
  expandedOffers: Record<string, boolean> = {};

  columns = [
    {
      name: "_id",
      required: true,
      label: "_id",
      field: "_id",
      align: "left",
      type: "string"
    },
    {
      name: "price",
      required: true,
      label: "price",
      field: "price",
      align: "left",
      type: "number",
      editable: true
    },
    {
      name: "priceInUsd",

      label: "priceInUsd",
      field: "priceInUsd",
      align: "left",
      type: "number",
      editable: true
    },
    {
      name: "offerId",
      align: "center",
      label: "offerId",
      field: "offerId",
      editable: true,
      type: "string"
    },
    {
      // Ячейка — слот body-cell-steamItemsIds выше
      name: "steamItemsIds",
      align: "left",
      label: "steamItemsIds",
      field: "steamItemsIds",
      type: "key-value"
    },
    {
      name: "partnerUserId",
      align: "center",
      label: "partnerUserId",
      field: "partnerUserId",
      type: "string"
    },
    {
      name: "type",
      align: "center",
      label: "type",
      field: "type",
      type: "string"
    },
    {
      name: "appId",
      align: "center",
      label: "appId",
      field: "appId",
      type: "number"
    },
    {
      name: "steamBotId",
      align: "center",
      label: "steamBotId",
      field: "steamBotId",
      type: "string"
    },
    {
      name: "partnerSteamId",
      align: "center",
      label: "partnerSteamId",
      field: "partnerSteamId",
      type: "string"
    },
    {
      name: "state",
      align: "center",
      label: "state",
      field: "state",
      editable: true,
      type: "number"
    },
    {
      name: "createdAt",
      align: "center",
      label: "createdAt",
      field: "createdAt",
      type: "string"
    },
    {
      name: "paymentId",
      align: "center",
      label: "paymentId",
      field: "paymentId",
      type: "string",
      editable: true
    },
    {
      name: "userId",
      align: "center",
      label: "userId",
      field: "userId",
      type: "string",
      editable: true
    },
    {
      name: "updatedAt",
      align: "center",
      label: "updatedAt",
      field: "updatedAt",
      type: "string"
    },
    {
      name: "settlementAt",
      align: "center",
      label: "settlementAt",
      field: "settlementAt",
      type: "string"
    }
  ];

  get lastSelected() {
    return _.last(this.selected) || null;
  }

  get partnersById() {
    return _.keyBy(this.partners, partner => String(partner._id));
  }

  isDopplerFilter(condition) {
    return _.isEqual(condition.steamItemsIds, DOPPLER_CONDITION);
  }

  // Поле условия поиска: value — поставить, null — убрать; остальные не трогаем
  withField(condition, field: string, value) {
    return value === null
      ? _.omit(condition, field)
      : { ...condition, [field]: value };
  }

  copyId(value: string) {
    copyToClipboard(value)
      .then(() =>
        this.$q.notify({
          message: `Скопировано: ${value}`,
          type: "positive",
          timeout: 1200
        })
      )
      .catch(() =>
        this.$q.notify({ message: "Не удалось скопировать", type: "negative" })
      );
  }

  // Одинаковые скины оффера — одной строкой с количеством
  offerItems(row) {
    const items = new Map<string, OfferItem>();
    (row.steamItemsIds || []).forEach((name: string, index: number) => {
      const item = items.get(name);
      if (item) {
        item.count++;
      } else {
        items.set(name, {
          name,
          price: (row.steamItemsPrices || [])[index] || 0,
          count: 1
        });
      }
    });
    return Array.from(items.values());
  }

  visibleOfferItems(row) {
    const items = this.offerItems(row);
    return this.expandedOffers[row._id]
      ? items
      : items.slice(0, OFFER_ITEMS_PREVIEW);
  }

  toggleOffer(offerId: string) {
    this.$set(this.expandedOffers, offerId, !this.expandedOffers[offerId]);
  }

  formatPrice(price: number) {
    return Number(price).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }

  // Картинки и редкость скинов текущей страницы — одним запросом
  async loadSteamItems(docs: any[]) {
    const names = _.uniq(
      _.flatMap(docs || [], doc => doc.steamItemsIds || [])
    ).filter(name => !(name in this.steamItems));

    if (!names.length) {
      return;
    }

    try {
      this.steamItems = {
        ...this.steamItems,
        ...(await fetchSteamItems(this.$apollo, names))
      };
    } catch (error) {
      this.$q.notify({
        message: `Не удалось загрузить картинки скинов: ${error.message}`,
        multiLine: true,
        type: "negative"
      });
    }
  }
}
